import { StackScreenProps } from '@react-navigation/stack'
import { orderBy } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IconButton } from 'react-native-paper'
import { useGlobalStyles } from '../../../../contexts/global-styles.provider'

import { AppNavigatorParams } from '../../../../navigation/types'
import { SocketEvents, useSocketRoom } from '../../../../socket/socket.hooks'
import { MelddTaskTargetType } from '../../../tasks/task.types'
import { ClusterCard } from './components/cluster-card'
import { ClusterCards, ClusterCardsRef } from './components/cluster-cards-list'
import { useTranslation } from 'react-i18next'
import { LevelIndicator } from '../shared/LevelIndicator'
import {
  MButton,
  Row,
  ScreenWrapper,
  Tutorial,
  TutorialStep,
} from '../../../../@ui'
import { useNode } from '../../../../contexts/current-node.provider'
import { ReplayButton } from '../../replay/components/reply.button'
import { TaskPendingIcon } from '../../../tasks/components/task-pending-icon'
import { useFocusEffect } from '@react-navigation/native'
import EagleView from '../../replay/shared/eagle-view'
import { TutorialKey } from '../../../../@ui/containers/others'
import { TargetType } from '../../perspectives/perspective-cards/types'
import {
  useAvailableClustersLazyQuery,
  useClusterByIdLazyQuery,
} from '../../../../generated/graphql'
import { MButtonEventIndicator } from '../../../../@ui/buttons/m-button'

export function ClusterCardsScreen({
  navigation,
  route,
}: StackScreenProps<AppNavigatorParams, 'ClusterCards'>) {
  const { params } = route
  const clusterId = params?.clusterId
  const nodeId = params?.nodeId
  const [eagleViewOpen, setEagleViewOpen] = useState(false)
  const globalStyles = useGlobalStyles()
  const [selectedClusterIndex, setSelectedClusterIndex] = useState<number>(0)
  const clusterCardsRef = useRef<ClusterCardsRef>(null)

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('eagle-view') === 'true'
    ) {
      setEagleViewOpen(true)
    }
  }, [])

  const [doLoadClusters, { data, loading: refreshing }] =
    useAvailableClustersLazyQuery()
  const [doLoadCluster] = useClusterByIdLazyQuery()

  const { setCurrentNode } = useNode()

  const clusters = useMemo(
    () =>
      orderBy(data?.availableClusters || [], ['contentUpdatedAt'], ['desc']),
    [data]
  )

  useEffect(() => {
    async function loadNode() {
      if (nodeId) {
        setCurrentNode(nodeId)
        return
      }
      // If node ID is not set (for example when coming from task)
      // We try to load the cluster and get the node ID from the cluster
      if (clusterId) {
        const { data } = await doLoadCluster({ variables: { id: clusterId } })

        if (data?.clusterById?.nodeId) {
          navigation.replace('ClusterCards', {
            clusterId: clusterId,
            nodeId: data?.clusterById?.nodeId,
          })
          return
        }
      }
      // If all fails go to List Nodes
      navigation.replace('ListNodes')
    }
    loadNode()
  }, [nodeId])

  const doRefresh = useCallback(async () => {
    if (!nodeId) {
      return
    }
    await doLoadClusters({
      variables: { input: { nodeId } },
      fetchPolicy: 'network-only',
    })
  }, [nodeId])

  // Reload every time the screen is focused (avoid cached issues adding or removing clusters)
  useFocusEffect(
    useCallback(() => {
      doRefresh()
    }, [])
  )

  useSocketRoom(nodeId).event(SocketEvents.Refresh, doRefresh)

  useEffect(() => {
    doRefresh()
  }, [nodeId])

  useEffect(() => {
    if (clusters.length === 0) {
      return
    }
    const index = clusters.findIndex((c) => c.id === clusterId)
    // if index is -1, set to 0
    setSelectedClusterIndex(Math.max(0, index))
  }, [clusterId, clusters])

  return (
    <ScreenWrapper withScrollView={false} style={globalStyles.pageContainer}>
      <Row
        justifyContent="space-between"
        alignItems="center"
        style={{ padding: 16 }}
      >
        <IconButton
          mode={'outlined'}
          icon="arrow-left"
          onPress={() => navigation.push('ClusterList', { nodeId })}
        />
        <Row gap={2} alignItems="center" columnGap={4}>
          <LevelIndicator
            target={TargetType.Cluster}
            targetId={clusters[selectedClusterIndex]?.id}
            style={{ marginLeft: 8 }}
            onEagleViewClick={() => setEagleViewOpen(!eagleViewOpen)}
            eagleViewOpen={eagleViewOpen}
          />
          <TaskPendingIcon
            targetId={clusters[selectedClusterIndex]?.id}
            targetType={MelddTaskTargetType.CLUSTER_TASK}
            size={22}
          />
          <ReplayButton
            targetId={clusters[selectedClusterIndex]?.id}
            targetType={TargetType.Cluster}
          />
        </Row>
      </Row>

      {nodeId && (
        <ClusterCards
          ref={clusterCardsRef}
          clusters={clusters}
          nodeId={nodeId}
          selectedClusterIndex={selectedClusterIndex}
          setSelectedClusterIndex={setSelectedClusterIndex}
          renderCard={(cluster, index) => (
            <ClusterCard
              key={index}
              cluster={cluster}
              onRefresh={doRefresh}
              refreshing={refreshing}
              onPerspectivePress={(perspective) => {
                navigation.navigate('Perspective', {
                  articleId: perspective.id,
                })
              }}
              navigateToContributions={(perspectiveId, contributionType) => {
                navigation.navigate('PerspectiveContributions', {
                  contributionTypeFilter: contributionType,
                  articleId: perspectiveId,
                })
              }}
            />
          )}
        />
      )}
      <EagleView
        targetId={clusters[selectedClusterIndex]?.id ?? ''}
        open={eagleViewOpen}
        onClose={() => setEagleViewOpen(false)}
        shareParams={
          clusterId && nodeId
            ? {
                targetType: TargetType.Cluster,
                targetId: clusterId,
                nodeId,
              }
            : undefined
        }
      />
      {clusters[selectedClusterIndex] && !eagleViewOpen && (
        <>
          <MButtonEventIndicator nodeId={nodeId} />

          <MButton
            targetType={MelddTaskTargetType.CLUSTER_TASK}
            targetId={clusters[selectedClusterIndex]?.id}
            preview={clusters[selectedClusterIndex]?.topic}
          />
        </>
      )}
      <ClusterCardTutorial />
    </ScreenWrapper>
  )
}

function ClusterCardTutorial() {
  const { t } = useTranslation('tutorials')

  const tutorialSteps: TutorialStep[] = [
    {
      tutorialId: 'create_perspective',
      message: t('create_perspective'),
    },
    {
      tutorialId: 'vote_perspective',
      message: t('vote_perspective'),
    },
  ]

  return (
    <Tutorial steps={tutorialSteps} tutorialKey={TutorialKey.CLUSTER_CARD} />
  )
}
export default ClusterCardsScreen
